import React, { useCallback, useRef, useState } from 'react';
import { Button, DataGrid, Modal } from '../../design-system';
import {
  downloadSwHeightTemplate,
  importSwHeightLogs,
  saveSwHeightLogs
} from '../../api/data';
import {
  SwHeightDatasetKind,
  SwHeightSuggestedSettings,
  WellLogImportPreview
} from '../../@types/swHeight/types';
import styles from './SwHeightImportModal.module.css';

type Props = {
  open: boolean;
  onClose: () => void;
  projectUid: string | null;
  /**
   * Called after the parsed data is persisted. `suggested` carries the
   * calibration constants found in the file, but only when the user
   * ticked the apply box -- nothing is applied silently.
   */
  onImported: (
    suggested?: SwHeightSuggestedSettings
  ) => Promise<void> | void;
};

/** Both input sheets are read from one file, in this order. */
const DATASET_KINDS: SwHeightDatasetKind[] = ['log', 'core'];

const DATASET_TITLE: Record<SwHeightDatasetKind, string> = {
  log: 'Well logs',
  core: 'Core / RCAL'
};

const COLUMN_HINT =
  'Upload an Excel workbook with one row per depth. A "Log Domain input ' +
  'Data" sheet and a "Core Domain input Data" sheet are both read if ' +
  'present. Required columns: MD, PERM and PHIT (or PHIE / Poro). ' +
  'Optional: TVDSS, SWT / Lab Sw, GR, CALI, RHOB, RESD, VSH.';

/**
 * The parser emits this when the sheet it picked is named for the other
 * dataset. A workbook with only one recognisable sheet parses successfully
 * for *both* kinds -- the single-candidate fallback returns the same sheet
 * either way -- so without this check a log-only file would also be saved
 * as core data, scattering bogus "core" points across the Sw track.
 */
const MISMATCH_WARNING = 'looks like it belongs to a different dataset';

const isMismatched = (preview: WellLogImportPreview) =>
  preview.warnings.some(warning => warning.includes(MISMATCH_WARNING));

export type DetectedDataset = {
  kind: SwHeightDatasetKind;
  preview: WellLogImportPreview;
};

/**
 * Keep one entry per real sheet. When both kinds resolved to the same
 * worksheet, only the kind whose name actually matched is kept; if neither
 * matched, the file is treated as well logs.
 */
export const dedupeDetected = (
  detected: DetectedDataset[]
): DetectedDataset[] => {
  const bySheet = new Map<string, DetectedDataset[]>();
  detected.forEach(entry => {
    const sheet = entry.preview.sheet_name ?? '';
    bySheet.set(sheet, [...(bySheet.get(sheet) ?? []), entry]);
  });

  const kept: DetectedDataset[] = [];
  bySheet.forEach(entries => {
    if (entries.length === 1) {
      kept.push(entries[0]);
      return;
    }
    const matched = entries.filter(entry => !isMismatched(entry.preview));
    kept.push(matched.length === 1 ? matched[0] : entries[0]);
  });
  return kept.sort(
    (a, b) => DATASET_KINDS.indexOf(a.kind) - DATASET_KINDS.indexOf(b.kind)
  );
};

const isExcelFile = (file: File) => /\.(xls|xlsx)$/i.test(file.name);

// Both grids can hold ten rows. Bounding them keeps the header pinned and
// puts the scrollbar on the table itself rather than the whole modal.
const CURVE_GRID_MAX_HEIGHT = 220;
const PREVIEW_GRID_MAX_HEIGHT = 260;

const formatCell = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : String(value);

const DatasetPreview: React.FC<{ entry: DetectedDataset }> = ({ entry }) => {
  const { kind, preview } = entry;
  const detectedColumns = Object.entries(preview.detected_columns);
  const previewColumns = Object.keys(preview.preview_rows[0] ?? {});

  return (
    <div className={styles.dataset} data-testid={`import-dataset-${kind}`}>
      <p className={styles.summary}>
        <strong>{DATASET_TITLE[kind]}</strong>
        {
          `${preview.sheet_name ? ` — sheet "${preview.sheet_name}"` : ''}: ` +
          `${preview.row_count} rows, ${detectedColumns.length} curves detected.`
        }
      </p>
      {preview.warnings.length > 0 && (
        <ul className={styles.warnings}>
          {preview.warnings.map(warning => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
      {preview.ignored_columns?.length > 0 && (
        <p className={styles.ignored}>
          Ignored {preview.ignored_columns.length} unrecognised column(s):{' '}
          {preview.ignored_columns.join(', ')}
        </p>
      )}
      <DataGrid
        title="Detected curves"
        maxHeight={CURVE_GRID_MAX_HEIGHT}
        columns={[
          {
            key: 'curve',
            header: 'Curve',
            getValue: (row: { curve: string; column: string }) =>
              row.curve.toUpperCase()
          },
          {
            key: 'column',
            header: 'Excel column',
            getValue: (row: { curve: string; column: string }) => row.column
          }
        ]}
        data={detectedColumns.map(([curve, column]) => ({ curve, column }))}
        rowKey="curve"
      />
      <DataGrid
        className={styles.previewGrid}
        title="Preview (first rows)"
        maxHeight={PREVIEW_GRID_MAX_HEIGHT}
        columns={previewColumns.map(name => ({
          key: name,
          header: name.toUpperCase(),
          getValue: (row: Record<string, number | null>) =>
            formatCell(row[name])
        }))}
        data={preview.preview_rows}
        rowKey={(_, index) => index}
      />
    </div>
  );
};

export const SwHeightImportModal: React.FC<Props> = ({
  open,
  onClose,
  projectUid,
  onImported
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [detected, setDetected] = useState<DetectedDataset[]>([]);
  const [filename, setFilename] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applySuggested, setApplySuggested] = useState(true);

  const resetState = useCallback(() => {
    setDetected([]);
    setFilename(null);
    setParsing(false);
    setSaving(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleFile = useCallback(
    async (file: File) => {
      if (!projectUid) return;
      if (!isExcelFile(file)) {
        setError('Only .xls/.xlsx files are supported.');
        return;
      }
      setParsing(true);
      setError(null);
      try {
        // Ask for both sheets; either may legitimately be absent.
        const reasons: string[] = [];
        const parsed = await Promise.all(
          DATASET_KINDS.map(kind =>
            importSwHeightLogs(projectUid, file, kind)
              .then(preview => ({ kind, preview }))
              .catch(parseError => {
                const reason =
                  parseError instanceof Error ? parseError.message : '';
                if (reason && !reasons.includes(reason)) reasons.push(reason);
                return null;
              })
          )
        );
        const found = dedupeDetected(
          parsed.filter((entry): entry is DetectedDataset => entry !== null)
        );
        if (found.length === 0) {
          // Both attempts failed. Surface why -- a generic "no sheet found"
          // hides the actionable part ("Required curve PERM not found").
          setError(
            reasons.length > 0
              ? reasons.join(' ')
              : 'No usable input sheet found. Expected a "Log Domain input ' +
                  'Data" or "Core Domain input Data" sheet with MD, PERM and ' +
                  'a porosity column.'
          );
          return;
        }
        setDetected(found);
        setFilename(file.name);
      } finally {
        setParsing(false);
      }
    },
    [projectUid]
  );

  // The log dataset is what gets calibrated, so its constants are the ones
  // worth applying.
  const suggested =
    detected.find(entry => entry.kind === 'log')?.preview
      .suggested_settings ?? {};
  const hasSuggestions =
    suggested.fwl !== undefined || suggested.grad_diff !== undefined;

  const handleSave = useCallback(async () => {
    if (!projectUid || detected.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      for (const entry of detected) {
        await saveSwHeightLogs(projectUid, entry.preview, entry.kind);
      }
      await onImported(
        applySuggested && hasSuggestions ? suggested : undefined
      );
      handleClose();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : 'Failed to save the imported data.'
      );
      setSaving(false);
    }
  }, [
    projectUid,
    detected,
    onImported,
    handleClose,
    applySuggested,
    hasSuggestions,
    suggested
  ]);

  const handleTemplateDownload = useCallback(async () => {
    try {
      const blob = await downloadSwHeightTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sw_height_input_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (templateError) {
      setError(
        templateError instanceof Error
          ? templateError.message
          : 'Failed to download template.'
      );
    }
  }, []);

  const hasPreview = detected.length > 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import data"
      size="lg"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasPreview || saving}
            data-testid="confirm-import"
          >
            {saving
              ? 'Saving…'
              : `Save ${detected.length > 1 ? 'both datasets' : 'data'}`}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        {!hasPreview && (
          <div className={styles.dropzone}>
            <p>{COLUMN_HINT}</p>
            <div className={styles.dropzoneActions}>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={parsing || !projectUid}
              >
                {parsing ? 'Parsing…' : 'Choose file'}
              </Button>
              <Button variant="secondary" onClick={handleTemplateDownload}>
                Download template
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              hidden
              data-testid="well-log-file-input"
              onChange={event => {
                const file = event.target.files?.[0];
                if (file) handleFile(file);
                event.target.value = '';
              }}
            />
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}

        {hasPreview && (
          <div className={styles.preview}>
            <p className={styles.summary} data-testid="import-summary">
              {filename ?? 'File'}:{' '}
              {detected.length > 1
                ? 'both input sheets detected.'
                : '1 input sheet detected.'}
            </p>
            {hasSuggestions && (
              <div
                className={styles.suggestions}
                data-testid="import-suggestions"
              >
                <p className={styles.suggestionsTitle}>
                  Calibration constants found in the file
                </p>
                <ul className={styles.suggestionsList}>
                  {suggested.fwl !== undefined && (
                    <li>Free water level (TVDSS): {suggested.fwl}</li>
                  )}
                  {suggested.grad_diff !== undefined && (
                    <li>Gradient difference: {suggested.grad_diff} psi/ft</li>
                  )}
                </ul>
                <label className={styles.suggestionsToggle}>
                  <input
                    type="checkbox"
                    checked={applySuggested}
                    data-testid="apply-suggested-settings"
                    onChange={event =>
                      setApplySuggested(event.target.checked)
                    }
                  />
                  Apply these when saving
                </label>
              </div>
            )}
            {detected.map(entry => (
              <DatasetPreview key={entry.kind} entry={entry} />
            ))}
            <Button variant="secondary" onClick={resetState}>
              Choose another file
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SwHeightImportModal;
